export type NormalizedAssetType = 'image' | 'video' | 'audio';

const IMAGE_EXTENSIONS = new Set([
  'jpg',
  'jpeg',
  'png',
  'gif',
  'webp',
  'bmp',
  'svg',
  'avif',
  'heic',
]);

const VIDEO_EXTENSIONS = new Set([
  'mp4',
  'mov',
  'webm',
  'avi',
  'm4v',
  'mkv',
  'mpeg',
  'mpg',
]);

const AUDIO_EXTENSIONS = new Set([
  'mp3',
  'wav',
  'aac',
  'ogg',
  'm4a',
  'flac',
]);

const EXTENSION_TO_MIME: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  bmp: 'image/bmp',
  svg: 'image/svg+xml',
  avif: 'image/avif',
  heic: 'image/heic',
  mp4: 'video/mp4',
  mov: 'video/quicktime',
  webm: 'video/webm',
  avi: 'video/x-msvideo',
  m4v: 'video/x-m4v',
  mkv: 'video/x-matroska',
  mpeg: 'video/mpeg',
  mpg: 'video/mpeg',
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  aac: 'audio/aac',
  ogg: 'audio/ogg',
  m4a: 'audio/mp4',
  flac: 'audio/flac',
};

export function isNormalizedAssetType(value: unknown): value is NormalizedAssetType {
  return value === 'image' || value === 'video' || value === 'audio';
}

export function getAssetExtension(value?: string | null): string | null {
  if (!value) return null;
  const normalized = value.toLowerCase();
  const cleaned = normalized.split('#')[0].split('?')[0];
  const match = cleaned.match(/\.([a-z0-9]{2,5})$/);
  return match?.[1] ?? null;
}

export function inferMimeTypeFromName(value?: string | null): string | null {
  const ext = getAssetExtension(value);
  if (!ext) return null;
  return EXTENSION_TO_MIME[ext] ?? null;
}

export function inferAssetType(params: {
  type?: string | null;
  mimeType?: string | null;
  name?: string | null;
  filename?: string | null;
  storageUrl?: string | null;
  previewUrl?: string | null;
  thumbnailUrl?: string | null;
}): NormalizedAssetType {
  const normalizedType = params.type?.toLowerCase();
  if (isNormalizedAssetType(normalizedType)) return normalizedType;

  const mimeType = (params.mimeType ?? '').toLowerCase();
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';

  const candidates = [
    params.name,
    params.filename,
    params.storageUrl,
    params.previewUrl,
    params.thumbnailUrl,
  ].filter((candidate): candidate is string => !!candidate);

  for (const candidate of candidates) {
    const ext = getAssetExtension(candidate);
    if (!ext) continue;
    if (IMAGE_EXTENSIONS.has(ext)) return 'image';
    if (VIDEO_EXTENSIONS.has(ext)) return 'video';
    if (AUDIO_EXTENSIONS.has(ext)) return 'audio';
  }

  return 'image';
}
